import api from "./api";
import { NO_SCOPE } from "./requestScope";
import { dedupedRead, invalidateRead } from "./readCache";
import { resolveAppId } from "./appScope";
import { unpackFieldProperties, normalizeAllDataFields } from "./fieldPayload";

// Workflow + version-scoped endpoints (B6/S1, extracted from api.js).
// Sub-module imports `api from './api'` like the other domain modules —
// the cycle is benign: bodies dereference the import at call time only.

// Every version-scoped route lives under /apps/:appId/versions/:versionUid/.
// Callers pass { appId } explicitly (from useParams or props); resolveAppId
// (services/appScope.js — shared with screensApi.js) sniffs the URL only as a
// safety net for call sites missed by the B6 migration.

// ── Field property pack/unpack helpers ──
// Core keys stay at top level; everything else goes into `properties` JSONB.
const FIELD_CORE_KEYS = new Set([
  "uid",
  "id",
  "name",
  "data_type",
  "type",
  "mandatory",
  "columns",
  "mapped_workflow_field",
  "properties",
]);

/** Before sending to API: move type-specific keys into `properties`. */
const packFieldProperties = (field) => {
  const core = {};
  const props = {};
  for (const [k, v] of Object.entries(field)) {
    // Skip transient UI-only properties (never send to backend)
    if (k.startsWith("_")) continue;
    // Cleared by a patch (e.g. unlinkGlobalFieldPatch dropping inherited
    // config) — omit rather than sending an undefined-valued key.
    if (v === undefined) continue;
    // Server-provided transient — must not be echoed back into `properties`.
    // `usage` only ever appears when the field was loaded via
    // ?include=usage (see fetchWorkflowDataFields) — it's report-only.
    if (k === "mapped_activity_field_count" || k === "usage") continue;
    if (k === "columns") {
      core.columns = (v || []).map(packFieldProperties);
    } else if (FIELD_CORE_KEYS.has(k)) {
      core[k] = v;
    } else {
      props[k] = v;
    }
  }
  if (Object.keys(props).length > 0) core.properties = props;
  return core;
};

// The receive-side counterpart (unpackFieldProperties) lives in
// ./fieldPayload alongside normalizeAllDataFields, so the view GETs' embedded
// catalog and this module's endpoints share exactly one implementation.

export const fetchWorkflows = async (appId, { signal } = {}) => {
  if (signal) {
    const response = await api.get(`/apps/${appId}/workflows`, { signal });
    return response.data;
  }
  return dedupedRead(`workflows|${appId}`, async () => {
    const response = await api.get(`/apps/${appId}/workflows`, NO_SCOPE);
    return response.data;
  });
};

// Returns the registered companion-table columns for a workflow. Sources the
// wf-lookup prefilter picker — restricts the picker to fields the runtime can
// actually filter on. wfId can be either the integer ID or the workflow UUID.
// 404 means the target workflow has no companion table yet — the picker
// should surface "add a record view to the target workflow first."
export const fetchCompanionColumns = async (appId, wfId, { signal } = {}) => {
  const response = await api.get(`/apps/${appId}/workflows/${wfId}/companion-columns`, { signal });
  return response.data;
};

// Returns the FULL schema of the target workflow shaped like companion
// columns. Sources the wf-lookup *display-field* picker so authors can
// pick any field on the target, not only fields already materialized into
// its companion table. Deploy materializes the picks (see studio-backend
// deployment_handler pre-pass + rv_table_builder extraFields).
export const fetchLookupTargetFields = async (appId, wfId, { signal } = {}) => {
  const response = await api.get(`/apps/${appId}/workflows/${wfId}/lookup-target-fields`, { signal });
  return response.data;
};

// Read-only companion-table inspector — table metadata + per-column source
// attribution. Powers the Config tab's "Data Table" sub-section.
export const fetchCompanionTableInfo = async (appId, wfId, { signal } = {}) => {
  const response = await api.get(`/apps/${appId}/workflows/${wfId}/companion-table-info`, { signal });
  return response.data;
};

export const fetchVersions = async (wfId, appId, { signal } = {}) => {
  if (signal) {
    const response = await api.get(`/apps/${appId}/workflows/${wfId}/versions`, {
      signal,
    });
    return response.data;
  }
  return dedupedRead(`versions|${wfId}|${appId ?? ""}`, async () => {
    const response = await api.get(`/apps/${appId}/workflows/${wfId}/versions`, NO_SCOPE);
    return response.data;
  });
};

// cutVersion freezes the workflow's current latest version (v_n becomes
// is_published=true) and creates a fresh draft v_(n+1) seeded with the
// diagram, triggers, prefills, view templates, and view permissions of v_n.
// Returns { id, uid, version_number, is_published } for the new draft.
//
// appId is required when wfId is a UUID because the same workflow UUID can
// appear in multiple apps (clone roundtrips, branch clones); the backend
// uses (uid, app_id) to disambiguate. Numeric wfId is unique on its own and
// doesn't need appId, but it's harmless to pass either way.
//
// Server returns 409 with `branch_cannot_cut` if the app is a branch — Cut
// Version is main-only. Returns 404 if the workflow has no version yet.
export const cutVersion = async (wfId, appId, { commitMessage = '' } = {}) => {
  // New draft version + freeze — version list and workflow latest-pointer change.
  invalidateRead(`versions|${wfId}`);
  invalidateRead("workflows");
  const params = appId ? { app_id: appId } : {};
  const response = await api.post(
    `/workflows/${wfId}/versions/cut`,
    { commit_message: commitMessage },
    { params },
  );
  return response.data;
};

// Callers inside an app-scoped route (URL contains /apps/N/) can omit appId
// — it's inferred from window.location (with a dev warning). Callers from
// non-app routes (e.g. the AI Employee detail page at
// /orgs/:orgId/ai-employees/:id) MUST pass appId explicitly, otherwise the
// URL collapses to /apps//versions/... → 400.
export const fetchCoreDiagram = async (versionId, { appId } = {}) => {
  const resolvedAppId = resolveAppId(appId);
  return dedupedRead(
    `coreDiagram|${resolvedAppId}|${versionId}`,
    async () => {
      const response = await api.get(
        `/apps/${resolvedAppId}/versions/${versionId}/core-diagram`,
        NO_SCOPE,
      );
      return response.data;
    },
  );
};

export const updateCoreDiagram = async (versionId, data, { appId } = {}) => {
  // Diagram changed — drop any cached core-diagram for this version.
  invalidateRead((k) => k.startsWith("coreDiagram|") && k.endsWith(`|${versionId}`));
  const response = await api.put(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/core-diagram`,
    data,
  );
  return response.data;
};

export const updateWorkflowDataFields = async (versionId, data, { appId } = {}) => {
  const resolvedAppId = resolveAppId(appId);
  // A save makes both the plain list and the ?include=usage variant stale —
  // drop both cached reads for this version so the next fetch (Reload, or a
  // tab re-entry) doesn't hand back pre-save data for up to READ_CACHE_TTL_MS.
  invalidateRead((k) => k.startsWith(`workflowDataFields|${resolvedAppId}|${versionId}|`));
  const response = await api.put(
    `/apps/${resolvedAppId}/versions/${versionId}/workflow-data-fields`,
    { fields: data.map(packFieldProperties) },
  );
  return response.data;
};

// includeUsage folds in what used to be the separate GET .../global-field-usage
// endpoint: each returned field carries a `usage` summary (activity_count +
// usages) of which activities map it, and the response also carries the
// version's full `activities` list — so the response becomes { fields, activities }
// instead of a bare array. Deduped via readCache: the Global Variables tab's own
// data load (Editor.js) and its usage hook (useGlobalFieldUsage) both request this
// exact key at the same time, so they collapse into ONE real request instead of
// hitting two separate endpoints.
export const fetchWorkflowDataFields = async (versionId, { appId, includeUsage } = {}) => {
  const resolvedAppId = resolveAppId(appId);
  const key = `workflowDataFields|${resolvedAppId}|${versionId}|${includeUsage ? "usage" : "plain"}`;
  const data = await dedupedRead(key, async () => {
    const response = await api.get(
      `/apps/${resolvedAppId}/versions/${versionId}/workflow-data-fields`,
      includeUsage ? { ...NO_SCOPE, params: { include: "usage" } } : NO_SCOPE,
    );
    return response.data;
  });
  // dedupedRead may hand the SAME cached object to multiple callers within the
  // TTL window — never mutate it in place (a second unpack on already-unpacked
  // fields would be a bug waiting to happen); always build a fresh result.
  if (Array.isArray(data)) return data.map(unpackFieldProperties);
  if (data?.fields) return { ...data, fields: data.fields.map(unpackFieldProperties) };
  return data;
};

export const fetchActivityDataFields = async (versionId, { appId } = {}) => {
  const response = await api.get(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/activity-data-fields`,
  );
  const data = response.data;

  // Backend now returns { activity_data_fields: [...], deployed_field_ids: [...] }
  // but many consumers expect a bare array — extract just the array here.
  const actArray =
    data?.activity_data_fields || (Array.isArray(data) ? data : []);
  return actArray.map((activity) => ({
    ...activity,
    data_fields: (activity.data_fields || []).map(unpackFieldProperties),
  }));
};

/** Combined fetch: workflow fields + activity fields + deployed IDs in one request. */
export const fetchAllDataFields = async (versionId, { appId } = {}) => {
  const response = await api.get(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/all-data-fields`,
  );
  return normalizeAllDataFields(response.data);
};


/** Fetch the global fields used by one activity (for the Fields-tab filter). */
export const fetchActivityGlobalFields = async (versionId, activityUid, { appId } = {}) => {
  const { data } = await api.get(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/activities/${activityUid}/global-fields`,
  );
  return data?.fields || [];
};

/** Fetch all data fields by workflow ID (resolves latest version on backend). */
// App-scoped for the same reason as updateWorkflow: a wf_lookup target lives in
// the SAME app, and on a branch the unscoped route returned main's field list.
export const fetchAllDataFieldsByWorkflow = async (workflowId, { appId } = {}) => {
  const response = await api.get(`/apps/${resolveAppId(appId)}/workflows/${workflowId}/all-data-fields`);
  // Deliberately keeps returning two keys, not the normalizer's three — its
  // three callers destructure exactly these.
  const { workflowDataFields, activityDataFields } = normalizeAllDataFields(response.data);
  return { workflowDataFields, activityDataFields };
};

/** Same as fetchActivityDataFields but also returns deployed_field_ids metadata. */
export const fetchActivityDataFieldsWithMeta = async (versionId, { appId } = {}) => {
  const response = await api.get(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/activity-data-fields`,
  );
  const data = response.data;

  const actArray =
    data?.activity_data_fields || (Array.isArray(data) ? data : []);
  return {
    activity_data_fields: actArray.map((activity) => ({
      ...activity,
      data_fields: (activity.data_fields || []).map(unpackFieldProperties),
    })),
    deployed_field_ids: data?.deployed_field_ids || [],
  };
};

export const updateActivityDataFields = async (versionId, data, { appId } = {}) => {
  const packed = Array.isArray(data)
    ? data.map((activity) => ({
        ...activity,
        data_fields: (activity.data_fields || []).map(packFieldProperties),
      }))
    : data;
  const response = await api.put(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/activity-data-fields`,
    packed,
  );
  return response.data;
};

export const fetchActivityPermissions = async (versionId, { appId } = {}) => {
  const response = await api.get(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/activity-permissions`,
  );
  return response.data;
};

export const updateActivityPermissions = async (versionId, data, { appId } = {}) => {
  const response = await api.put(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/activity-permissions`,
    data,
  );
  return response.data;
};

export const fetchActivityPermissionsByUid = async (versionId, activityUid, { appId } = {}) => {
  const response = await api.get(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/activities/${activityUid}/permissions`,
  );
  return response.data;
};

export const updateActivityPermissionByUid = async (
  versionId,
  activityUid,
  data,
  { appId } = {},
) => {
  const response = await api.put(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/activities/${activityUid}/permissions`,
    data,
  );
  return response.data;
};

// ── View-template RBAC (RV / DV / Chart Template) ──
export const fetchStatesByVersionId = async (versionId, { appId } = {}) => {
  const response = await api.get(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/states`,
  );
  return response.data;
};

export const fetchActivityDataFieldsByUid = async (versionId, activityUid, { appId } = {}) => {
  const response = await api.get(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/activities/${activityUid}/data-fields`,
  );
  const data = response.data;
  if (Array.isArray(data)) return data.map(unpackFieldProperties);
  if (data?.data_fields)
    data.data_fields = data.data_fields.map(unpackFieldProperties);
  return data;
};

export const updateActivityDataFieldsByUid = async (
  versionId,
  activityUid,
  data,
  options = {},
) => {
  const packed = Array.isArray(data)
    ? data.map(packFieldProperties)
    : {
        ...data,
        data_fields: (data.data_fields || []).map(packFieldProperties),
      };
  // When the caller has confirmed deletion of fields with downstream
  // references (SAFETY_VIOLATION → modal → "Delete & Save"), switch to
  // the wrapper shape so the backend knows which UIDs are approved.
  // Legacy save-without-confirmation path continues to POST the bare
  // array.
  const body = options.allowedDeleteFieldUIDs?.length
    ? {
        fields: Array.isArray(packed) ? packed : packed.data_fields || [],
        allowed_delete_field_uids: options.allowedDeleteFieldUIDs,
      }
    : packed;
  const response = await api.put(
    `/apps/${resolveAppId(options.appId)}/versions/${versionId}/activities/${activityUid}/data-fields`,
    body,
  );
  return response.data;
};

export const fetchActivityUIConfig = async (versionId, activityUid, { appId } = {}) => {
  const response = await api.get(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/activities/${activityUid}/ui-config`,
  );
  return response.data;
};

export const updateActivityUIConfig = async (versionId, activityUid, data, { appId } = {}) => {
  const response = await api.put(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/activities/${activityUid}/ui-config`,
    data,
  );
  return response.data;
};

export const fetchCreationConstraints = async (versionId, { appId } = {}) => {
  const response = await api.get(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/creation-constraints`,
  );
  return response.data;
};

export const updateCreationConstraints = async (versionId, constraints, { appId } = {}) => {
  const response = await api.put(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/creation-constraints`,
    { constraints },
  );
  return response.data;
};

export const fetchActivityTriggerByUid = async (versionId, activityUid, { appId } = {}) => {
  const response = await api.get(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/activities/${activityUid}/triggers`,
  );
  return response.data;
};

// Activity notes — markdown text per activity. Auto-generated by V1 import
// (source='v1_import') or hand-written by users (source='manual'). Saving
// always flips source to 'manual' on the backend.
export const fetchActivityNote = async (versionId, activityUid, { appId } = {}) => {
  const response = await api.get(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/activities/${activityUid}/notes`,
  );
  return response.data;
};

export const updateActivityNote = async (versionId, activityUid, content, { appId } = {}) => {
  const response = await api.put(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/activities/${activityUid}/notes`,
    { content },
  );
  return response.data;
};

// Activity AI Settings — per-activity AI Employee prompt fragment.
// Surfaced verbatim in the renderer's prompt under "### Instructions" when
// the AI Employee is woken to process this activity.
export const fetchActivityAISettings = async (versionId, activityUid, { appId } = {}) => {
  const response = await api.get(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/activities/${activityUid}/ai-settings`,
  );
  return response.data;
};

export const updateActivityAISettings = async (versionId, activityUid, aiInstructions, { appId } = {}) => {
  const response = await api.put(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/activities/${activityUid}/ai-settings`,
    { ai_instructions: aiInstructions },
  );
  return response.data;
};

// Appendices — sidecar attachments hanging off an activity note. Used to
// hold bulky content (V1 JS bodies, JSON dumps) and user-authored subnotes
// without bloating the main markdown.
export const createActivityNoteAppendix = async (
  versionId,
  activityUid,
  payload,
  { appId } = {},
) => {
  const response = await api.post(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/activities/${activityUid}/notes/appendix`,
    payload,
  );
  return response.data;
};

export const updateActivityNoteAppendix = async (
  versionId,
  activityUid,
  appendixId,
  payload,
  { appId } = {},
) => {
  const response = await api.patch(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/activities/${activityUid}/notes/appendix/${appendixId}`,
    payload,
  );
  return response.data;
};

export const deleteActivityNoteAppendix = async (
  versionId,
  activityUid,
  appendixId,
  { appId } = {},
) => {
  await api.delete(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/activities/${activityUid}/notes/appendix/${appendixId}`,
  );
};

export const updateActivityTriggerByUid = async (
  versionId,
  activityUid,
  data,
  { appId } = {},
) => {
  const response = await api.put(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/activities/${activityUid}/triggers`,
    data,
  );
  return response.data;
};

export const fetchActivityPrefillByUid = async (versionId, activityUid, { appId } = {}) => {
  const response = await api.get(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/activities/${activityUid}/prefill`,
  );
  return response.data;
};

export const updateActivityPrefillByUid = async (
  versionId,
  activityUid,
  data,
  { appId } = {},
) => {
  const response = await api.put(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/activities/${activityUid}/prefill`,
    data,
  );
  return response.data;
};

// Activity Display Fields — the activity-level registry of resolved
// values the grid designer places by uid (Phase B: studio panel).
// Backend persists rows in tbl_wf_activity_display_fields keyed by uid;
// PUT upserts and deletes any row whose uid isn't present in the payload.
export const fetchActivityDisplayFields = async (versionId, activityUid, { appId } = {}) => {
  const response = await api.get(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/activities/${activityUid}/display-fields`,
  );
  return response.data;
};

// displaySource is OPTIONAL: omitting it leaves the stored source untouched
// server-side, so a caller that only edits the inline rows cannot accidentally
// reset an activity to inline mode.
export const updateActivityDisplayFields = async (
  versionId,
  activityUid,
  displayFields,
  { appId, displaySource } = {},
) => {
  const body = { display_fields: displayFields };
  if (displaySource) body.display_source = displaySource;
  const response = await api.put(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/activities/${activityUid}/display-fields`,
    body,
  );
  return response.data;
};

// Field Actions (stored in ui-config alongside field_rules)
export const fetchFieldActions = async (versionId, activityUid, { appId } = {}) => {
  const response = await api.get(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/activities/${activityUid}/ui-config`,
  );
  return response.data;
};

export const updateFieldActions = async (versionId, activityUid, data, { appId } = {}) => {
  const response = await api.put(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/activities/${activityUid}/ui-config`,
    data,
  );
  return response.data;
};

export const fetchActivityConfigByUid = async (versionId, activityUid, { appId } = {}) => {
  const response = await api.get(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/activities/${activityUid}/config`,
  );
  return response.data;
};

export const updateActivityConfigByUid = async (
  versionId,
  activityUid,
  data,
  { appId } = {},
) => {
  const response = await api.put(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/activities/${activityUid}/config`,
    data,
  );
  return response.data;
};

export const createActivity = async (versionId, data, { appId } = {}) => {
  invalidateRead((k) => k.startsWith("coreDiagram|") && k.endsWith(`|${versionId}`));
  const response = await api.post(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/activities`,
    data,
  );
  return response.data;
};

export const fetchActivityUsage = async (versionId, activityUid, { appId } = {}) => {
  const response = await api.get(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/activities/${activityUid}/usage`,
  );
  return response.data;
};

export const fetchActivityImpact = async (versionId, activityUid, { appId } = {}) => {
  const response = await api.get(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/activities/${activityUid}/impact`,
  );
  return response.data;
};

export const fetchActivityFieldImpact = async (
  versionId,
  activityUid,
  fieldSid,
  { appId } = {},
) => {
  const response = await api.get(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/activities/${activityUid}/fields/${fieldSid}/impact`,
  );
  return response.data;
};

export const deleteActivity = async (versionId, activityUid, { appId } = {}) => {
  invalidateRead((k) => k.startsWith("coreDiagram|") && k.endsWith(`|${versionId}`));
  const response = await api.delete(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/activities/${activityUid}`,
  );
  return response.data;
};

export const fetchActivities = async (versionId, { appId } = {}) => {
  const response = await api.get(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/activities`,
  );
  return response.data;
};

export const fetchFullWorkflowConfig = async (versionId, { appId } = {}) => {
  const response = await api.get(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/full-config`,
  );
  return response.data;
};

/**
 * Every activity's saved trigger graph for this version — the slice of
 * full-config the gateway edge Rule Editor actually needs (to enumerate
 * Response-node `response.data.<key>` fields), without the rest of
 * full-config's states/transitions/data-fields/permissions/prefills/diagram.
 */
export const fetchActivityTriggerFields = async (versionId, { appId } = {}) => {
  const response = await api.get(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/activity-trigger-fields`,
  );
  return response.data;
};

export const fetchDataApiDocs = async (versionId, { appId } = {}) => {
  const response = await api.get(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/data-api-docs`,
  );
  return response.data;
};

export const createWorkflow = async (appId, data) => {
  invalidateRead(`workflows|${appId}`);
  const response = await api.post(`/apps/${appId}/workflows`, data);
  return response.data;
};

export const deleteWorkflow = async (appId, wfId) => {
  invalidateRead(`workflows|${appId}`);
  invalidateRead(`versions|${wfId}`);
  const response = await api.delete(`/apps/${appId}/workflows/${wfId}`);
  return response.data;
};

// App-scoped: a workflow uid is unique per app (branch and clone preserve it),
// so without :appId the backend resolved the oldest match and a branch's rename
// edited its PARENT app's workflow.
export const updateWorkflow = async (wfId, data, { appId } = {}) => {
  invalidateRead("workflows");
  invalidateRead(`versions|${wfId}`);
  const response = await api.put(`/apps/${resolveAppId(appId)}/workflows/${wfId}`, data);
  return response.data;
};

// ── versionId-based view fetchers ───────────────────────────────────────────
// These four take { appId } like the rest of the version cluster (B6/S1
// completed the Tier 2 #9b migration). Their appId-based siblings
// (fetchRecordViewsByApp, etc.) live in viewsApi.js.
export const fetchRecordViews = async (versionId, { appId } = {}) => {
  const response = await api.get(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/record-views`,
  );
  return response.data;
};

// View API - Detail Views
export const fetchDetailViews = async (versionId, { appId } = {}) => {
  const response = await api.get(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/detail-views`,
  );
  return response.data;
};

export const fetchActivityViewsByWorkflow = async (versionId, { appId } = {}) => {
  const response = await api.get(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/activity-views`,
  );
  return response.data;
};

export const fetchChartTemplates = async (versionId, { appId } = {}) => {
  const response = await api.get(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/chart-templates`,
  );
  return response.data;
};

/**
 * The keys that are LIVE in the deployed companion table — studio's 🔒
 * deploy-lock.
 *
 * NOT an occupancy index. It says which keys exist in production data, not which
 * keys are taken in this version. Never derive availability from it: that is
 * resolveFieldKeys' single job, and a second answer to it is what mislabelled
 * 165 grid columns before 2026-08-25.
 *
 * → string[]
 */
export const fetchDeployedFieldKeys = async (versionId, { appId } = {}) => {
  const { data } = await api.get(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/deployed-field-keys`,
  );
  return data?.deployed_field_ids || [];
};

/**
 * THE field-key endpoint. One call answers three questions:
 *   - allocate: give me N fresh `<prefix>_<n>` keys
 *   - desired:  is this label/key usable, and if not, what is?
 *
 * It replaces GET .../field-keys (a version-wide key list the browser used to
 * hold and reason over) and POST .../field-keys/check. There is deliberately no
 * client-side occupancy logic any more: the code that PROPOSES a key is now the
 * code that JUDGES it, so a proposal cannot be rejected by the judge. That is
 * what made a field labelled "Order" offer the key `order` and then refuse it.
 *
 * body: { scope: { kind, parent_uid? }, activity_uid?, own_uid?,
 *         pending_keys: [{ key, uid, parent_uid? }],
 *         requests: [{ id, allocate: { prefix, count } } | { id, desired }] }
 *
 * → { results: [{ id, keys, desired?, available?, reason?, held_by? }] }
 *   keys[0] is always usable. `available` is ABSENT on an allocate result —
 *   there is no verdict about a key the server itself chose.
 *   reason is "held_in_version" | "duplicate_in_payload" | "reserved" | "invalid".
 */
export const resolveFieldKeys = async (versionId, body, { appId } = {}) => {
  const { data } = await api.post(
    `/apps/${resolveAppId(appId)}/versions/${versionId}/field-keys/resolve`,
    body,
  );
  return { results: data?.results || [] };
};
